import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

/**
 * Ce întoarce ExpertAccounts pentru un tip de document, brut.
 *
 * Solicitare: prețul de pus pe documentul de primire e `pin` de pe `tc-out`-ul pereche.
 *
 * Are dreptate — dar întrebarea e dacă `pin` chiar ajunge la noi prin API, sau rămâne doar în
 * tabela lor. Endpointul de citire exista deja, fără ecran; fără el, singurul mod de a afla era
 * să pun o linie de jurnal pe server și să aștept o livrare.
 *
 * Se întorc și NUMELE câmpurilor, nu doar valorile: când cauți un câmp al cărui nume nu-l
 * cunoști, lista numelor e jumătate din răspuns.
 *
 * Rămâne aici și după: același drum servește registrul de bonuri de consum (`doct=bnc`) și
 * orice altă întrebare de tipul „ce știe EA despre documentul ăsta".
 */
interface Raspuns {
  doct: string
  linii: Record<string, unknown>[]
  campuri: string[]
}

interface RaspunsStoc {
  locid: string | null
  total: number
  campuri: string[]
  randuri: Record<string, unknown>[]
}

function azi(): string {
  return new Date().toISOString().slice(0, 10)
}

export function DocumenteBruteEa() {
  const [doct, setDoct] = useState('tc-out')
  const [de, setDe] = useState(azi())
  const [pana, setPana] = useState(azi())
  /**
   * Coloane cerute anume.
   *
   * `get_invdocs` citește din `inv_documents`, unde lotul CHIAR există — `lotid`, `batch`,
   * `lotsrc` — dar returnează doar o selecție de coloane, iar ele nu-s în ea. Dacă endpointul
   * acceptă `fields`, lotul se poate cere. Dacă nu, îl ignoră și nu strică nimic.
   */
  const [campuri, setCampuri] = useState('')
  const [rezultat, setRezultat] = useState<Raspuns | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const [gestiune, setGestiune] = useState('1')
  const [stoc, setStoc] = useState<RaspunsStoc | null>(null)

  const citeste = useMutation({
    mutationFn: async () =>
      (await api.get<Raspuns>('/ea/documente', { params: { doct, de, pana }, timeout: 60_000 }))
        .data,
    onSuccess: (r) => {
      setRezultat(r)
      setEroare(null)
    },
    onError: (e: unknown) => {
      const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response
      setEroare(r?.data?.error?.message ?? 'Nu am putut citi documentele')
      setRezultat(null)
    },
  })

  /**
   * Stocul, brut.
   *
   * Costul se citește din documente; lotul nu — `infolot` vine gol acolo. Dar stocul se ține pe
   * loturi, iar `new_docIN` acceptă `infolot` pe linie. Dacă feedul de stoc aduce codul lotului,
   * documentul de primire îl poate purta, și marfa nu-și mai pierde batch-ul.
   */
  const citesteStoc = useMutation({
    mutationFn: async () =>
      (
        await api.get<RaspunsStoc>('/ea/stoc-brut', {
          params: { locid: gestiune || undefined },
          timeout: 60_000,
        })
      ).data,
    onSuccess: (r) => {
      setStoc(r)
      setEroare(null)
    },
    onError: (e: unknown) => {
      const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response
      setEroare(r?.data?.error?.message ?? 'Nu am putut citi stocul')
      setStoc(null)
    },
  })

  return (
    // Fără titlu, cartonașul nu se putea găsi pe o pagină lungă: se vedea doar un text explicativ.
    <Card
      title="Documente brute din EA"
      description="Ce întoarce ExpertAccounts pentru un tip de document, exact așa cum vine. Arată ce câmpuri ajung la noi prin API — de exemplu dacă se poate citi costul de intrare de pe un transfer."
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Tip document</span>
          <input
            value={doct}
            onChange={(e) => setDoct(e.target.value)}
            placeholder="tc-out"
            className="w-32 rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">De la</span>
          <input
            type="date"
            value={de}
            onChange={(e) => setDe(e.target.value)}
            className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Până la</span>
          <input
            type="date"
            value={pana}
            onChange={(e) => setPana(e.target.value)}
            className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
            Coloane în plus (opțional)
          </span>
          <input
            value={campuri}
            onChange={(e) => setCampuri(e.target.value)}
            placeholder="batch,lotid"
            className="w-40 rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </label>
        <Button
          variant="secondary"
          onClick={() => citeste.mutate()}
          disabled={citeste.isPending || !doct.trim()}
        >
          {citeste.isPending ? 'Citesc…' : 'Citește din EA'}
        </Button>
      </div>

      <p className="mt-1 text-[11px] text-b4l-text-muted">
        Tipuri uzuale: <code>tc-out</code> (transfer, ieșire) · <code>tc-in</code> (primire) ·{' '}
        <code>bnc</code> (bon de consum) · <code>fac</code> (factură).
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-b4l-border pt-3">
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Stocul din gestiunea</span>
          <input
            value={gestiune}
            onChange={(e) => setGestiune(e.target.value)}
            placeholder="1"
            className="w-24 rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </label>
        <Button
          variant="secondary"
          onClick={() => citesteStoc.mutate()}
          disabled={citesteStoc.isPending}
        >
          {citesteStoc.isPending ? 'Citesc…' : 'Citește stocul brut'}
        </Button>
        <span className="text-[11px] text-b4l-text-muted">
          Aici se vede dacă EA ne dă lotul (batch, termen de valabilitate) pe stoc.
        </span>
      </div>

      {stoc && (
        <div className="mt-2 space-y-2">
          <p className="text-sm">
            <strong>{stoc.total}</strong> rânduri de stoc · <strong>{stoc.campuri.length}</strong>{' '}
            câmpuri
          </p>
          <div className="rounded-lg bg-b4l-bg p-2">
            <p className="mb-1 text-[11px] font-medium uppercase text-b4l-text-muted">
              Câmpuri returnate
            </p>
            <p className="font-mono text-xs leading-relaxed">{stoc.campuri.join(' · ')}</p>
          </div>
          <details>
            <summary className="cursor-pointer text-sm text-b4l-text-muted">
              Primele {stoc.randuri.length} rânduri, brut
            </summary>
            <pre className="mt-1 max-h-96 overflow-auto rounded-lg bg-b4l-bg p-2 text-[11px]">
              {JSON.stringify(stoc.randuri, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {eroare && <p className="mt-2 text-sm text-b4l-red">{eroare}</p>}

      {rezultat && (
        <div className="mt-3 space-y-2">
          <p className="text-sm">
            <strong>{rezultat.linii.length}</strong> linii ·{' '}
            <strong>{rezultat.campuri.length}</strong> câmpuri
          </p>

          {/* Numele câmpurilor, separat: de obicei asta e întrebarea, nu valorile. */}
          <div className="rounded-lg bg-b4l-bg p-2">
            <p className="mb-1 text-[11px] font-medium uppercase text-b4l-text-muted">
              Câmpuri returnate
            </p>
            <p className="font-mono text-xs leading-relaxed">{rezultat.campuri.join(' · ')}</p>
          </div>

          <details open={rezultat.linii.length <= 5}>
            <summary className="cursor-pointer text-sm text-b4l-text-muted">
              Liniile, brut ({rezultat.linii.length})
            </summary>
            <pre className="mt-1 max-h-96 overflow-auto rounded-lg bg-b4l-bg p-2 text-[11px]">
              {JSON.stringify(rezultat.linii, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  )
}
